import React from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';

interface OasisFatalErrorsDialogProps {
  open: boolean;
  /** Null when only the backend's refusal is known, not the count. */
  fatalCount: number | null;
  /** The chart is locked, so its Live Scrubber is not on screen. */
  locked: boolean;
  /** Whether this user can reach the errors: open the scrubber, or unlock. */
  canFix: boolean;
  onClose: () => void;
  onFix: () => void;
  /**
   * Set when the agency does not require a clean chart for this signer: the
   * dialog warns instead of refusing, and this continues to the signature.
   */
  onProceed?: () => void;
  proceedLabel?: string;
}

/**
 * Shown before the signature dialog when the chart has All CMS scrubber
 * fatals. Refuses when the agency requires a clean chart for this signer;
 * otherwise warns and lets them continue.
 */
export const OasisFatalErrorsDialog: React.FC<OasisFatalErrorsDialogProps> = ({
  open,
  fatalCount,
  locked,
  canFix,
  onClose,
  onFix,
  onProceed,
  proceedLabel,
}) => {
  const blocking = !onProceed;
  const errors =
    fatalCount === null
      ? 'errors'
      : `${fatalCount} ${fatalCount === 1 ? 'error' : 'errors'}`;

  return (
    <Dialog open={open} onOpenChange={next => !next && onClose()}>
      <DialogContent className='sm:max-w-[480px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-foreground'>
            <AlertTriangle className='h-5 w-5 shrink-0 text-amber-600' />
            {blocking
              ? 'Fix OASIS errors before signing'
              : 'This chart has OASIS errors'}
          </DialogTitle>
          <DialogDescription className='space-y-2 pt-2 text-sm'>
            <span className='block'>
              This chart has {errors} that CMS will reject.
              {blocking &&
                ' Your agency requires them to be fixed before signing.'}
            </span>
            {locked && (blocking || canFix) && (
              <span className='block'>
                {canFix
                  ? 'Edit the chart to see and fix them in the Live Scrubber.'
                  : 'Someone who can edit this chart needs to fix them.'}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose}>
            Close
          </Button>
          {canFix && (
            <Button
              type='button'
              variant={blocking ? 'default' : 'outline'}
              onClick={onFix}
            >
              {locked ? 'Edit chart' : 'Show errors'}
            </Button>
          )}
          {onProceed && (
            <Button type='button' onClick={onProceed}>
              {proceedLabel ?? 'Sign anyway'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
